/*
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * ENGINEERING NOTES
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * 1) Do not import any classes from the moment.js library (e.g. with
 *    import { moment } from 'moment'). This is becausethe moment.js classes can
 *    be very large and webpack has issues compressing them properly.
 * 2) Instead import the moment.js classes you need via HTML <script> tags.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * IMPORTANT: Always import helper classes individually and not via the EzHelpers
 * module. This is because the EzHelpers module imports all helper classes and can
 * cause circular dependencies and increase bundle size.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * IMPORTANT: Always import Enumeration classes individually and not via the
 * EzEnumerations module. This is because the EzEnumerations module imports all
 * enumeration classes and can cause circular dependencies and increase bundle size.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * WARNING: Do not import the following into this class to avoid circular
 * dependencies:
 *     import { ... } from '/ezlibrary/helpers/EzHelpers.js'
 *     import { EzApi } from '/public/common/javascript/ezapi.js';
 * import { EzUI } from '/public/common/javascript/ezui.js';
 * import { ezUI } from '/public/common/javascript/ezui.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * WARNING: Do not import the following helper classes into this class to avoid
 * circular dependencies:
 *     import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
 * import { EzArray } from '/ezlibrary/helpers/EzArray.js';
 *     import { EzMap } from '/ezlibrary/helpers/EzMap.js';
 * import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
 *     import { EzError } from '/ezlibrary/helpers/EzError.js';
 *     import {
 *         DAY_IN_MONTH,
 *         EzDays
 *     } from '/ezlibrary/helpers/EzDays.js';
 * import { EzJson } from '/ezlibrary/helpers/EzJson.js';
 *     import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
 *     import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
 *     import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
 *     import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
 *     import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
 *     import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
 * import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
 *     import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
 *     import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */

import {
    decode,
    encode
} from 'html-entities';
import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';
import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzChar } from '/ezlibrary/helpers/EzChar.js';
import { EzRegEx } from '/ezlibrary/helpers/EzRegEx.js';

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * A class of static utility methods and/or properties for string types.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into non-helper classes with:
 *     import {
 *         // ... other helper classes ...,
 *         EzString
 *     } from '/ezlibrary/helpers/EzHelpers.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other helper classes (that allow it) with:
 *     import { EzString } from '/ezlibrary/helpers/EzString.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Static access:
 *     EzString.{property or method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzString extends EzStaticClass {
    /**
     * @static
     * @public @function
     * Uppercases the character at the provided aIndex index in the aValue string and returns the result.
     * @param {string} [aValue = EzString.EMPTY]
     * @param {number} [aIndex = EzNumber.FIRST_INDEX]
     * @return {string}
     */
    static upperCaseAtIndex(aValue = EzString.EMPTY, aIndex = EzNumber.FIRST_INDEX) {
        if (!EzString.hasText(aValue)) {
            throw new EzBadParamException(
                { aValue },
                EzString,
                EzString.upperCaseAtIndex);
        }
        if (!EzNumber.isNumber(aIndex)) {
            throw new EzBadParamException(
                { aIndex },
                EzString,
                EzString.upperCaseAtIndex);
        }
        if (EzNumber.FIRST_INDEX > aIndex || aValue.length <= aIndex) {
            throw new EzBadParamException(
                { aIndex },
                EzString,
                EzString.upperCaseAtIndex,
                `The provided aIndex param must be greater than or equal to ${EzNumber.FIRST_INDEX} and less than ${aValue.length}.`);
        }

        return `${aValue.slice(EzNumber.FIRST_INDEX, aIndex)}${aValue.charAt(aIndex).toUpperCase()}${aValue.slice(aIndex + EzNumber.ONE)}`;
    }

    /**
     * @static
     * @public @function
     * Uppercases the first character of the provided aValue string and returns the result.
     * @param {string} [aValue = EzString.EMPTY]
     * @return {string}
     */
    static upperCaseFirst(aValue = EzString.EMPTY) {
        return EzString.upperCaseAtIndex(aValue, EzNumber.FIRST_INDEX);
    }

    /**
     * @static
     * @public @function
     * Uppercases the first character of the provided aValue string and returns the result.
     * Alias for: EzString.upperCaseFirst(aValue = EzString.EMPTY)
     * @param {string} [aValue = EzString.EMPTY]
     * @return {string}
     * @deprecated
     * Migrate to: EzString.upperCaseFirst(aValue)
     */
    static upperCaseFirstChar = EzString.upperCaseFirst;

    /**
     * @static
     * @public @function
     * Uppercases the first character of the provided aValue string and returns the result.
     * Alias for: EzString.upperCaseFirst(aValue = EzString.EMPTY)
     * @param {string} [aValue = EzString.EMPTY]
     * @return {string}
     * @deprecated
     * Migrate to: EzString.upperCaseFirst(aValue)
     */
    static upperCaseFirstLetter = EzString.upperCaseFirst;

    /**
     * @static
     * @public @function
     * Lower cases the character at the provided aIndex index in the aValue string and returns the result.
     * @param {string} [aValue = EzString.EMPTY]
     * @param {number} [aIndex = EzNumber.FIRST_INDEX]
     * @return {string}
     */
    static lowerCaseAtIndex(aValue = EzString.EMPTY, aIndex = EzNumber.FIRST_INDEX) {
        if (!EzString.hasText(aValue)) {
            throw new EzBadParamException(
                { aValue },
                EzString,
                EzString.lowerCaseAtIndex);
        }
        if (!EzNumber.isNumber(aIndex)) {
            throw new EzBadParamException(
                { aIndex },
                EzString,
                EzString.lowerCaseAtIndex);
        }
        if (EzNumber.FIRST_INDEX > aIndex || aValue.length <= aIndex) {
            throw new EzBadParamException(
                { aIndex },
                EzString,
                EzString.lowerCaseAtIndex,
                `The provided aIndex param must be greater than or equal to ${EzNumber.FIRST_INDEX} and less than ${aValue.length}.`);
        }

        return `${aValue.slice(EzNumber.FIRST_INDEX, aIndex)}${aValue.charAt(aIndex).toLowerCase()}${aValue.slice(aIndex + EzNumber.ONE)}`;
    }

    /**
     * @static
     * @public @function
     * Lower cases the first character of the provided aValue;
     * @param {string} [aValue = EzString.EMPTY]
     * @returns {string}
     */
    static lowerCaseFirst(aValue = EzString.EMPTY) {
        return EzString.lowerCaseAtIndex(aValue, EzNumber.FIRST_INDEX);
    }

    /**
     * @static
     * @public @function
     * Returns an array of the provided strigns in sorted order by length.
     * @param {...string} stringsToSort
     * The strings to sort by length.
     * @returns {array}
     */
    static sortStringsByLength(...stringsToSort) {
        if (!EzObject.isValid(stringsToSort) || EzNumber.ZERO_LENGTH <= EzNumber.numberOrZero(stringsToSort?.length)) {
            return [];
        }

        let stringsToSortSet = new Set(stringsToSort);

        return [...stringsToSortSet].sort(
            (stringA, stringB) => stringB.length - stringA.length);
    }

    /**
     * @private @field
     * Stores the sorted (by length) array of the default knownWords.
     * @type {array}
     */
    static #defaultKnownWordsSet = EzString.sortStringsByLength([
        'ezclocker',
        'eznova',
        'employer',
        'employee',
        'payroll',
        'manager',
        'admin',
        'developer',
        'persona',
        'support',
        'account',
        'selected',
        'active',
        'payments',
        'payment',
        'production',
        'document',
        'license',
        'android',
        'amazon',
        'window',
        'google',
        'stage',
        'brain',
        'apple',
        'write',
        'https',
        'http',
        'html',
        'ipados',
        'load',
        'read',
        'save',
        'tree',
        'pay',
        'pal',
        'ios',
        'get',
        'aws',
        'dev',
        'loc',
        'prd',
        'e2e',
        'qal',
        'pqa',
        'stg',
        'ezn',
        'ez',
        'is',
        'date',
        'time',
        'period',
        'entry',
        'break',
        'off',
        'on'
    ]);

    /**
     * @private @field
     * Stores an array of 'known words' for use in camel casing and other various cases where it is desirable to preserve the integrity of certain words that
     * may be present in a string, such as 'pay' in 'payroll' or 'http' in 'https', etc.
     * @type {array}
     */
    static #knownWords = EzString.#defaultKnownWordsSet;
    /**
     * @static
     * @public @property @getter
     * Gets an array of 'known words' for use in camel casing and other various cases where it is desirable to preserve the integrity of certain words that
     * may be present in a string, such as 'pay' in 'payroll' or 'http' in 'https', etc.
     * @returns {array}
     */
    static get knownWords() {
        if (!EzObject.isValid(this.#knownWords) || EzNumber.ZERO_LENGTH >= EzNumber.numberOrZero(this.#knownWords?.length)) {
            this.#knownWords = this.#defaultKnownWordsSet;
        }

        return this.#knownWords;
    }
    /**
     * @static
     * @public @property @setter
     * This setter actually just append the provided additionalKnownWords to the existing #knownWords array, skipping any duplicate words.
     * @param {array} [additionalKnownWords=[]]
     */
    static set knownWords(additionalKnownWords = []) {
        if (!EzObject.isValid(additionalKnownWords) || EzNumber.ZERO_LENGTH >= EzNumber.numberOrZero(additionalKnownWords?.length)) {
            return;
        }

        if (!EzObject.isValid(this.#knownWords) || EzNumber.ZERO_LENGTH >= EzNumber.numberOrZero(this.#knownWords?.length)) {
            this.#knownWords = this.#defaultKnownWordsSet;
        }

        additionalKnownWords.forEach(
            (knownWord) => {
                // Avoid duplicates
                if (!this.#knownWords.includes(knownWord)) {
                    this.#knownWords.push(knownWord);
                }
            });

        // Sort the known words by length
        this.#knownWords = EzString.sortStringsByLength(this.#knownWords);
    }

    /**
     * @static
     * @public @function
     * Transforms the provided aValue string to a camelCase string and returns the result.
     * The split regex handles spaces, underscores, and hyphens as word delimiters. Each word after the first gets its first
     * character uppercased and the rest lowercased, so mixed-case input like ALREADYcamel is normalized correctly.
     * If custom words are provided, they are sorted longest-first so payments matches before payment and pay, https before http, etc.
     * A camelCase boundary split ((?<=[a-z])(?=[A-Z])) handles already-camelCased input that doesn't use delimiters
     * All matched tokens are lowercased before reassembly, normalizing input like HTTP → Http in non-first position
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Examples:
     * toCamelCase('ezclocker employer') // 'ezclockerEmployer'
     * toCamelCase('ezclockeremployee')  // 'ezclockerEmployee'
     * toCamelCase('getActiveEmployee')  // 'getActiveEmployee'
     * toCamelCase('isSelectedEmployer') // 'isSelectedEmployer'
     * toCamelCase('HTTP_request_url')   // 'httpRequestUrl'
     * toCamelCase('load_payroll_data')  // 'loadPayrollData'
     * toCamelCase('aws_dev_config')     // 'awsDevConfig'
     * toCamelCase('payments_manager')   // 'paymentsManager' (not 'payMentsManager')
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} [aValue = EzString.EMPTY]
     * @param {undefined|null|array} [customWords = []]
     * @returns {string}
     */
    static asCammelCase(aValue = EzString.EMPTY, customWords = []) {
        if (!EzString.isString(aValue)) {
            throw new EzBadParamException(
                { aValue },
                EzString,
                EzString.asCammelCase);
        }

        if (!EzString.hasText(aValue)) {
            return EzString.EMPTY;
        }

        // Append the custom words to the existing knownWords and store (if any)
        if (EzObject.isValid(customWords) && EzNumber.ZERO_LENGTH < EzNumber.numberOrZero(customWords?.length)) {
            EzString.knownWords = customWords;
        }

        const knownWordsPattern = EzRegEx.regExp_regExp_WORD_LIST_MATHCER(EzString.knownWords);

        const words = aValue.trim()
            // spaces, underscores, hyphens
            .split(EzRegEx.regExp_MATCH_SPACE_UNDERSCORE_DASH)
            // known word boundaries
            .flatMap(
                (token) => token.split(knownWordsPattern))
            // camelCase boundaries in remaining chunks
            .flatMap(
                (token) => token.split(EzRegEx.regExp_MATCH_POSITION_BETWEEN_LOWERCASE_CHAR_AND_UPPERCASE_CHAR))
            .filter(Boolean)
            .map(
                (word) => word.toLowerCase());

        return words.map(
            (word, index) => {
                return EzNumber.FIRST_INDEX === index
                    ? word
                    // Reassmble the word after applying camel case logic
                    : `${word.charAt(EzNumber.FIRST_INDEX).toUpperCase()}${word.slice(EzNumber.ONE)}`;
            })
            .join(EzString.EMPTY);
    }

    /**
     * @static
     * @public @function
     * Transforms the provided aValue string to a 'proper case' string wheren the string is camel cased, and the first character is lower case.
     * Similar to EzString.asCammelCase(aValue, customWords) with the addition of lower casing the first character of the final result to ensure proper
     * camel case formatting.
     * Note on the split and reassembly logic:
     * The split regex handles spaces, underscores, and hyphens as word delimiters. Each word after the first gets its first
     * character uppercased and the rest lowercased, so mixed-case input like ALREADYcamel is normalized correctly.
     * If custom words are provided, they are sorted longest-first so payments matches before payment and pay, https before http, etc.
     * A camelCase boundary split ((?<=[a-z])(?=[A-Z])) handles already-camelCased input that doesn't use delimiters
     * All matched tokens are lowercased before reassembly, normalizing input like HTTP → Http in non-first position
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Examples:
     * asProperCase('ezclocker employer') // 'ezclockerEmployer'
     * asProperCase('ezclockeremployee')  // 'ezclockerEmployee'
     * asProperCase('getActiveEmployee')  // 'getActiveEmployee'
     * asProperCase('isSelectedEmployer') // 'isSelectedEmployer'
     * asProperCase('HTTP_request_url')   // 'httpRequestUrl'
     * asProperCase('load_payroll_data')  // 'loadPayrollData'
     * asProperCase('aws_dev_config')     // 'awsDevConfig'
     * asProperCase('payments_manager')   // 'paymentsManager' (not 'payMentsManager')
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} [aValue = EzString.EMPTY]
     * @param {undefined|null|array} [customWords = []]
     * @returns {string}
     */
    static asProperCase(aValue = EzString.EMPTY, customWords = []) {
        return EzString.lowerCaseFirst(
            EzString.asCamelCase(aValue, EzArray.arrayOrEmpty(customWords)));
    }

    /**
     * @static
     * @public @readonly @property
     * Returns the empty string value
     * @returns {string}
     */
    static get EMPTY() {
        return EzChar.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Returns the provided aValue with all the space, \n, \r, and \f characters removed.
     * If the provided aValue is not a string, then the aValue is returned as provided.
     * @value {string} aValue
     * @returns {string}
     */
    static removeAllSpacesReturnsLineFeeds(aValue) {
        return EzString.hasLength(aValue)
            ? aValue.trim()
                .replaceAll(EzChar.space, EzChar.EMPTY)
                .replaceAll(EzChar.return, EzChar.EMPTY)
                .replaceAll(EzChar.new_line, EzChar.EMPTY)
                .replaceAll(EzChar.line_feed, EzChar.EMPTY)
            : aValue;
    }

    /**
     * @static
     * @public @function
     * Returns the provided aValue with all the space characters removed.
     * If the provided aValue is not a string, then the aValue is returned as provided.
     * @param {undefined|null|string|any} aValue
     * @returns {undefined|null|string|any}
     * Returns a string tryp unless the provided aValue is not a string, then returns the type of the provided aValue.
     */
    static removeAllSpaces(aValue) {
        return EzString.hasText(aValue)
            ? EzString.trim(aValue)
                .replaceAll(EzChar.space, EzChar.EMPTY)
            : aValue;
    }

    /**
     * @static
     * @public @method
     * Removes all \n and \r characters from the provided string
     * @value {string} value
     * @returns {string}
     */
    static removeAllReturnsLineFeeds(aValue) {
        return EzString.hasLength(aValue)
            ? aValue.replaceAll(EzChar.return, EzChar.EMPTY)
                .replaceAll(EzChar.line_feed, EzChar.EMPTY)
                .replaceAll(EzChar.form_feed, EzChar.EMPTY)
            : aValue;
    }

    /**
     * @static
     * @public @function
     * Replaces all the matching strings from the stringToReplace array in aValue with their corresponding strings (by index) from the replacementStrings array.
     * If aValue is not a string or is an empty string, then aValue is returned as provided.
     * If the stringsToReplace or replacementStrings arrays are not valid or empty, or if they are of different lengths, then an exception is thrown.
     * @param {undefined|null|string|any} aValue
     * @param {array} [stringsToReplace=EzArray.EMPTY]
     * @param {array} [replacementStrings=EzArray.EMPTY]
     * @return {undefined|null|string|any}
     */
    static replaceAll(aValue, stringsToReplace = EzArray.EMPTY, replacementStrings = EzArray.EMPTY) {
        if (!EzString.hasText(aValue) || (!EzArray.hasLength(stringsToReplace) && !EzArray.hasLength(replacementStrings))) {
            return aValue;
        }

        if (!EzObject.isValid(stringsToReplace) || EzNumber.ZERO_LENGTH >= EzNumber.numberOrZero(stringsToReplace?.length) ||
            stringsToReplace.length > EzNumber.numberOrZero(replacementStrings?.length)) {
            throw new EzBadParamException(
                { stringsToReplace },
                EzString,
                EzString.replaceAll);
        }
        if (!EzObject.isValid(replacementStrings) || EzNumber.ZERO_LENGTH >= EzNumber.numberOrZero(replacementStrings?.length) ||
            replacementStrings.length > EzNumber.numberOrZero(stringsToReplace?.length)) {
            throw new EzBadParamException(
                { replacementStrings },
                EzString,
                EzString.replaceAll);
        }

        let result = aValue;

        stringsToReplace.forEach(
            (stringToReplace, index) => {
                if (EzString.hasText(stringToReplace)) {
                    const replacementString = replacementStrings[index];

                    result = result.replaceAll(stringToReplace, replacementString);
                }
            });

        return result;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string 'n/a' placeholder for data that is 'not available'
     * @returns {string}
     * @deprecated
     * Migrate to:
     *  EzString.NA
     */
    static get na() {
        return EzString.NA;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string 'n/a' placeholder for data that is 'not available'
     * @returns {string}
     */
    static get NA() {
        return 'n/a';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string '-' placeholder for data that is 'not available'
     * @returns {string}
     */
    static get DASH() {
        return '-';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the map of HTML character code to character
     * Some mappings not supported (such as @nbsp; )
     * @returns {object}
     */
    static get HTML_ENCODED_CHAR_TO_CHAR_MAP() {
        return null;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the map of character to ASCII Integer value
     */
    static get CHAR_TO_ASCII_INTEGER_MAP() {
        return null;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided aObject is a string type
     * @param {undefined|null|string|any} aValue
     * @returns {boolean}
     */
    static isString(aValue) {
        return undefined !== aValue &&
            null !== aValue &&
            ('string' === typeof aValue || '[object String]' === Object.prototype.toString.call(aValue));
    }

    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string. Otherwise, returns an empty string.
     * @param {undefined|null|string|any} aValue
     * @returns {string}
     */
    static stringOrEmpty(aValue) {
        return EzString.stringOrDefault(aValue, EzString.EMPTY);
    }


    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string. Otherwise, returns null;
     * @param {undefined|null|string|any} aValue
     * @returns {string}
     */
    static stringOrNull(aValue) {
        return EzString.stringOrDefault(aValue, null);
    }

    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string, otherwise returns the provided aDefault value.
     * @param {undefined|null|string|any} aValue
     * @param {undefined|null|string|any} defaultValue
     * @returns {string}
     */
    static stringOrDefault(aValue, defaultValue) {
        return EzString.isString(aValue)
            ? aValue
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string, otherwise returns the provided aDefault value.
     * @param {*} aString
     * @param {undefined|null|string|*} aDefaultValue
     * Default: EzString.EMPTY
     * @returns {string}
     */
    static stringOrDefaultTrim(aString, aDefaultValue = EzString.EMPTY) {
        return EzString.isString(aString)
            ? aString.trim()
            : EzString.isString(aDefaultValue)
                ? aDefaultValue.trim()
                : aDefaultValue;
    }

    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string with a length. Otherwise, returns an empty string.
     * @param {*} aString
     * @returns {string}
     */
    static stringWithLengthOrEmpty(aString) {
        return EzString.hasLength(aString)
            ? aString
            : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string with a length. Otherwise, returns null;
     * @param {*} aString
     * @returns {string}
     */
    static stringWithLengthOrNull(aString) {
        return EzString.hasLength(aString)
            ? aString
            : null;
    }

    /**
     * @static
     * @public @method
     * Returns the provdied aString if it is a valid string with a length. Otherwise, returns an empty string.
     * @param {*} aString
     * @returns {string}
     */
    static stringWithLengthOrDefault(aString, aDefault) {
        return EzString.hasLength(aString)
            ? aString
            : aDefault;
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is a string with a length greater than zero then the aValue is returned.
     * If the provided value is a string with zero length OR the provided aValue is not string,
     * then the provided defaultValue is returned (the defaultValue is never trimmed when returned even if it is a string).
     * @param {undefined|null|string|any} aValue
     * @param {undefined|null|string|any} defaultValue
     * @returns {undefined|null|string|any}
     * Returns a trimmed version of the provided aValue if it is a string with a length greater than zero.
     * Otherwise, returns the provided defaultValue (the defaultValue is never trimmed when returned even if it is a string).
     */
    static stringWithLengthOrDefaultTrim(aValue, defaultValue) {
        const result = stringWithLengthOrDefault(aValue, defaultValue)

        return EzString.isString(result)
            ? result.trim()
            : result;
    }

    /**
     * @static
     * @public @function
     * If the provided aValue is a string, returns a trimmed version of the aValue.
     * Otherwise, returns the provide aValue.
     * @param {undefined|null|string|any} aValue
     * @returns {undefined|null|string|any}
     * Returns the trimmed version of provided aValue if it is a string.
     * Otherwise, returns the aValue.
     */
    static trim(aValue) {
        return EzString.trimOrDefault(aValue, aValue);
    }

    /**
     * @static
     * @public @method
     * If the provdied aString is a string, then the string is returned after trimming.
     * Otherwise, an empty string is returned.
     * @param {undefined|null|string} aValue
     * @returns {string}
     * Returns the trimmed version of provided aValue if it is a string.
     * Otherwise, returns EzString.EMPTY (empty string).
     */
    static trimOrEmpty(aValue) {
        return EzString.trimOrDefault(aValue, EzString.EMPTY);
    }

    /**
     * @static
     * @public @method
     * If the provdied aString is a string, then the string is returned after trimming.
     * Otherwise, null is returned.
     * @param {undefined|null|string} aValue
     * @returns {string}
     * Returns the trimmed version of provided aValue if it is a string.
     * Otherwise, returns null.
     */
    static trimOrNull(aValue) {
        return EzString.trimOrDefault(aValue, null);
    }

    /**
     * @static
     * @public @method
     * If the provdied aString is a string, then the string is returned after trimming.
     * Otherwise, the provided defaultValue is returned.
     * @param {undefined|null|string} aValue
     * @param {undefined|null|string|any} defaultValue
     * @returns {string}
     * Returns the trimmed version of provided aValue if it is a string.
     * Otherwise, returns the provided defaultValue (not trimed even if it is a string).
     */
    static trimOrDefault(aValue, defaultValue) {
        return EzString.isString(aValue)
            ? aValue.trim()
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * If the provdied value is a string and the string has a length then the value is returned.
     * Otherwise, the default value is returned.
     * @param {*} aString
     * @returns {string}
     */
    static textOrDefault(value, defaultValue) {
        return EzString.hasLength(value)
            ? value
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * Determines if the passed aValue is a string type AND the length is greater than zero.
     * @param {*} aValue
     * @returns {boolean}
     * @deprecated
     * Migrate to: EzString.hasLength(...)
     */
    static stringHasLength(aValue) {
        return EzString.hasLength(aValue);
    }

    /**
     * @static
     * @public @method
     * Removes and shorter method name fo EzString.hasLength(aValue)
     * Determines if the passed aValue is a string type AND the length is greater than zero.
     * @param {*} aValue
     * @returns {boolean}
     */
    static hasLength(aValue) {
        return EzString.isString(aValue) && undefined !== aValue && null !== aValue && 0 !== aValue.length;
    }

    /**
     * @static
     * @public @method
     * Determines if the provdied aValue has text and not just 'white space'
     * Supported Whitespace characters: ' ', '\t', '\n', and '\r'
     * Alias for: !EzString.isOnlyWhitespace(aValue)
     * @param {string} aValue
     * @returns {boolean}
     */
    static hasText(aValue) {
        return !EzString.isOnlyWhitespace(aValue);
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, the defualtValue is returned.
     * @param {string} aValue
     * @param {*|string} defaultValue
     * @returns {*|string}
     */
    static stringWithTextOrDefault(aValue, defaultValue) {
        return EzString.hasTextOrDefault(aValue, defaultValue);
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, the defualtValue is returned.
     * @param {string} aValue
     * @param {*|string} defaultValue
     * @returns {*|string}
     */
    static stringWithTextOrDefaultTrim(aValue, defaultValue) {
        return !EzString.isOnlyWhitespace(aValue)
            ? aValue.trim()
            : EzString.stringWithTextOrDefault(
                defaultValue.trim(),
                defaultValue);
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, the defualtValue is returned.
     * @param {string} aValue
     * @param {*|string} defaultValue
     * @returns {*|string}
     */
    static hasTextOrDefault(aValue, defaultValue) {
        return !EzString.isOnlyWhitespace(aValue)
            ? aValue
            : defaultValue;
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, nullis returned.
     * @param {string} aValue
     * @returns {null|string}
     */
    static stringWithTextOrNull(aValue = null) {
        return EzString.hasTextOrNull(aValue);
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, nullis returned.
     * @param {string} aValue
     * @returns {null|string}
     */
    static hasTextOrNull(aValue = null) {
        return !EzString.isOnlyWhitespace(aValue)
            ? aValue
            : null;
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, EzString.EMPTY is returned.
     * @param {string} aValue
     * @returns {*|string}
     */
    static stringWithTextOrEmpty(aValue) {
        return EzString.hasTextOrEmpty(aValue);
    }

    /**
     * @static
     * @public @method
     * If the provided aValue is not blank and does not only consist of supported white spae characters then
     * the aValue is returned. Otherwise, EzString.EMPTY is returned.
     * @param {string} aValue
     * @returns {*|string}
     */
    static hasTextOrEmpty(aValue) {
        return !EzString.isOnlyWhitespace(aValue)
            ? aValue
            : EzString.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Determines if the provdied aValue is empty or only consists of 'white space' characters
     * Supported Whitespace characters: ' ', '\t', '\n', and '\r'
     * @param {string} aValue
     * @returns {boolean}
     */
    static isOnlyWhitespace(aValue) {
        if (EzString.hasLength(aValue)) {
            for (const aChar of aValue) {
                if (' ' !== aChar && '\t' !== aChar && '\n' !== aChar && '\r' !== aChar) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * @public
     * Templated JS String
     * Example use: EzString.build`A string with template variable: ${var1}`;
     * Builds a templated string
     * 1) Removes all extra spaces and extra tabs (aka two tabs in a row will result in one tab) from the data.
     * 2) Trims the string before returning.
     * @param {array} aStrings
     * @param {array} aKeys
     * @returns {string}
     */
    static build(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzObject.isValid(aStrings)) {
            for (let x = 0;x < aStrings.length;x++) {
                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzObject.isValid(aKey)
                    ? aKey.toString()
                    : EzString.EMPTY;

                cleanValues = `${cleanValues}${aStrings[x]}${aKey}`;

                // Comment in the below to debug results
                // console.log(`aStrings[${x}]=${JSON.stringify(aString)}\naKeys[${x}]=${JSON.stringify(aKey)}\nnextValue=${JSON.stringify(nextValue)}\nStep #${x}=${JSON.stringify(cleanValues)}`);
            }
        }

        // Comment in the below to debug results
        /*
        console.log(`value=${JSON.stringify(cleanValues)}`);
        let trimedValue = cleanValues
            .replace(/(\t{2,})+/g, '\t')
            .replace(/( {2,})+/g, ' ')
            .trim();
        console.log(`trimedValue=${JSON.stringify(trimedValue)}`);
        return trimedValue;
        */

        return cleanValues.replace(/(\t{2,})+/g, '\t')
            .replace(/( {2,})+/g, ' ')
            .trim();
    }

    /**
     * @static
     * @public @method
     * Constructs an id with part as a timestamp.
     * Underscore only added if prefix and/or suffix is a string with length.
     * 1) If prefix and suffix is provided:
     *      Template: '{prefix}_{moment().format('x')}_{suffix}'
     * 2) If prefix is undefined, null, or empty string and suffix is a non-empty string:
     *      Template: '{moment().format('x')}_{suffix}'
     * 4) If prefix is a non-empty string and suffix is undefiend, null, or empty string:
     *      Template: '{prefix}_{moment().format('x')}'
     * 3) If prefix AND suffix is undefined, null, or empty string:
     *      Template: '{moment().format('x')}'
     * @param {undefined|null|string} prefix
     * Default: EzString.EMPTY
     * @param {undefined|null|string} suffix
     * Default: EzString.EMPTY
     * @returns {string}
     */
    static buildTimestampId(prefix = EzString.EMPTY, suffix = EzString.EMPTY) {
        const timestampId = EzString.hasLength(prefix)
            ? `${prefix}_${moment().format('x')}`
            : moment().format('x');

        return EzString.hasLength(suffix)
            ? `${timestampId}_${suffix}`
            : timestampId;
    }

    /**
     * @static @public
     * Templated JS String
     * Example use: EzString.em`A string with template variable: ${var1}`;
     * Alias to the EzString.msg custom Templated string handler.
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
     * the value with one space in front, once space at end and a line feed.
     * Result template: ' ${value_with_extra_space_removed} \n';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    static em(aStrings, ...aKeys) {
        return EzString.msg(aStrings, aKeys);
    }

    /**
     * @static @public
     * Templated JS String
     * Example use: EzString.msg`A string with template variable: ${var1}`;
     * Removes all extract space from the provided aTemplateLitteralValue param.
     * Returns the value with:
     * --> One space in front
     * --> One space at end
     * --> One line feed at the end
     * Result template: ' ${value_with_extra_space_removed} \n';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    static msg(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzObject.isValid(aStrings) && 0 !== aStrings.length) {
            for (let x = 0;x < aStrings.length;x++) {
                let aString = aStrings[x];

                if (EzNumber.isNumber(aString) || EzBoolean.isBoolean(aString)) {
                    aString = aString.toString();
                }

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzObject.isValid(aKey)
                    ? aKey.toString()
                    : EzString.EMPTY;

                // Remove line feeds, carrage returns, and tabs
                let cleanValue = aString.replace(EzRegEx.MATCH_ALL_LINEFEEDS, ' ');

                cleanValue = cleanValue.replace(EzRegEx.MATCH_ALL_RETURNS_AND_TABS, EzString.EMPTY);

                // Remove extra spaces
                cleanValue = cleanValue.replace(EzRegEx.MATCH_2_OR_MORE_SPACES, ' ');

                cleanValues = `${cleanValues}${cleanValue}${aKey}`;
            }
        }

        return cleanValues.replace(/( {2})+/g, ' ');
    }

    /**
     * @static
     * @public
     * Replaces the matching values with ''
     * Example: EzString.dropChars(/[ \t\n\r]+/g);
     * @param {string} aValue
     * @param {string} regex
     * Provide a valid regex expression
     */
    static drop(aString, regex) {
        if (!EzObject.isValid(regex)) {
            throw new EzBadParamException(
                'regex',
                EzString,
                EzString.drop);
        }
        if (!EzString.hasLength(aString)) {
            return aString;
        }

        return aString.replace(
            regex,
            EzString.EMPTY);
    }

    /**
     * @static
     * @public
     * Replaces all white space characters with empty string
     * @param {string} aValue
     */
    static dropSTNR(aValue) {
        if (!EzString.hasLength(aValue)) {
            return aValue;
        }

        return EzString.drop(aValue, EzRegEx.MATCH_ALL_WHITESPACE_CHARACTERS);
    }

    /**
     * @static
     * @public
     * Replaces all extra blank spaces, tabs, newlines, and returns with ''
     * @param {string} aValue
     */
    static dropXSTNR(aValue) {
        if (!EzString.hasLength(aValue)) {
            return aValue;
        }

        return EzString.drop(
            aValue,
            EzRegEx.MATCH_ALL_2SPACES_TAB_LINEFEED_RETURN);
    }


    /**
     * @static
     * @public
     * Replaces all tabs, newlines, and returns with ''
     * @param {string} aValue
     */
    static dropTNR(aValue) {
        if (!EzString.hasLength(aValue)) {
            return aValue;
        }

        return EzString.drop(
            aValue,
            EzRegEx.MATCH_ALL_TAB_LINEFEED_RETURN);
    }

    /**
     * @static
     * @public @method
     * Applies URL decoding to the provided string
     * If the provided value is undefined or null then an empty string is returned.
     * @param {string} value
     * @param {undefined|null|boolean} replacePlusWithSpace
     * If true, replaces any + symbol with a space
     * @returns {string}
     * @deprecated
     * Migrate to: EzUrl.decodeUrl(value, replacePlusWithSpace)
     */
    static decodeURL(value, replacePlusWithSpace) {
        if (!EzString.hasLength(value)) {
            return EzString.EMPTY;
        }

        return EzBoolean.isTrue(replacePlusWithSpace)
            ? decodeURIComponent(value)
                .replaceAll('+', ' ')
            : decodeURIComponent(value);
    }

    /**
     * @static
     * @public @method
     * Applies URL encoding to the provided string
     * If the provided value is undefined or null then an empty string is returned.
     * @param {string} value
     * @returns {string}
     * @deprecated
     * Migrate to: EzUrl.decodeUrl(value, replacePlusWithSpace)
     */
    static encodeURL(value) {
        if (!EzString.hasLength(value)) {
            return EzString.EMPTY;
        }

        return encodeURIComponent(value).replace(
            EzRegEx.MATCH_ALL_BANG_TICK_PARENTHESIS_ASTRIX,
            (c) => '%' + c.codePointAt(EzNumber.FIRST_INDEX).toString(EzNumber._16));
    }

    /**
     * @static
     * @public @method
     * Builds the encoding options object from the provided values for use with the html-entities library.
     * Use EzString.htmlEncode(value, buildDecodingOptions(...)) to encode a string
     * using the html-entities library.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Import html-entities library with:
     *      import { encode } from 'html-entities';
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Documentation:
     *  1) https://www.npmjs.com/package/html-entities\
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Ecnodeing Level
     *  1) all - alias to html5 (default).
     *  2) html5 - uses HTML5 named references.
     *  3) html4 - uses HTML4 named references.
     *  4) xml - uses XML named references.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Encoding Mode
     *  1) specialChars - encodes only HTML special characters (default).
     *  2) nonAscii - encodes HTML special characters and everything outside the ASCII character range.
     *  3) nonAsciiPrintable - encodes HTML special characters and everything outiside of the ASCII printable characters.
     *  4) nonAsciiPrintableOnly - everything outiside of the ASCII printable characters keeping HTML special characters intact.
     *  5) extensive - encodes all non-printable characters, non-ASCII characters and all characters with named references.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Numeric
     *  1) decimal - uses decimal numbers when encoding html entities. i.e. &#169; (default).
     *  2) hexadecimal - uses hexadecimal numbers when encoding html entities. i.e. &#xa9;.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Scope
     *  1) body - emulates behavior of browser when parsing tag bodies: entities without semicolon are also replaced (default).
     *  2) attribute - emulates behavior of browser when parsing tag attributes: entities without semicolon are replaced when not followed by equality sign =.
     *  3) strict - ignores entities without semicolon.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Examples
     *  encode('< > " \' & © ∆');
     *  // -> '&lt; &gt; &quot; &apos; &amp; © ∆'
     *
     *  encode('< ©', {mode: 'nonAsciiPrintable'});
     *  // -> '&lt; &copy;'
     *
     *  encode('< ©', {mode: 'nonAsciiPrintable', level: 'xml'});
     *  // -> '&lt; &#169;'
     *
     *  encode('< > " \' & ©', {mode: 'nonAsciiPrintableOnly', level: 'xml'});
     *  // -> '< > " \' & &#169;'
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} level
     * Default: 'all'
     * @param {string} mode
     * Default: 'specialChars'
     * @param {string} numeric
     * Default: 'decimal'
     * @returns {object}
     * Returns the following object:
     *  {
     *      level: {One of the following string values: 'all', 'html5', 'html4', or 'xml'},
     *      mode: {One of the following string values: 'specialChars', 'nonAscii', 'nonAsciiPrintable', 'nonAsciiPrintableOnly', 'extensive'},
     *      numeric: {One of the following string values: 'decimal', 'hexadecimal'},
     *  };
     */
    static buildEncodingOptions(level = 'all', mode = 'specialChars', numeric = 'decimal') {
        level = level || 'all';

        level = level.indexOf('all') || level.indexOf('html5') || level.indexOf('html4') || level.indexOf('xml')
            ? level
            : 'all';

        mode = mode || 'extensive';

        mode = mode.indexOf('specialChars') || mode.indexOf('nonAscii') || mode.indexOf('nonAsciiPrintable') || mode.indexOf('nonAsciiPrintableOnly') ||
        mode.indexOf('extensive')
            ? mode
            : 'specialChars';

        numeric = numeric || 'decimal';

        numeric = numeric.indexOf('decimal') || numeric.indexOf('hexadecimal')
            ? numeric
            : 'decimal';

        return {
            level,
            mode,
            numeric
        };
    }

    /**
     * @static
     * @public @method
     * Builds the encoding options object from the provided values for use with the html-entities library.
     * Use EzString.htmlDecode(value, buildDecodingOptions(...)) to decode a string
     * using the html-entities library.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Import html-entities library with:
     *      import { decode } from 'html-entities';
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Documentation:
     *  1) https://www.npmjs.com/package/html-entities\
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Ecnodeing Level
     *  1) all - alias to html5 (default).
     *  2) html5 - uses HTML5 named references.
     *  3) html4 - uses HTML4 named references.
     *  4) xml - uses XML named references.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Encoding Mode
     *  1) specialChars - encodes only HTML special characters (default).
     *  2) nonAscii - encodes HTML special characters and everything outside the ASCII character range.
     *  3) nonAsciiPrintable - encodes HTML special characters and everything outiside of the ASCII printable characters.
     *  4) nonAsciiPrintableOnly - everything outiside of the ASCII printable characters keeping HTML special characters intact.
     *  5) extensive - encodes all non-printable characters, non-ASCII characters and all characters with named references.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Numeric
     *  1) decimal - uses decimal numbers when encoding html entities. i.e. &#169; (default).
     *  2) hexadecimal - uses hexadecimal numbers when encoding html entities. i.e. &#xa9;.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Scope
     *  1) body - emulates behavior of browser when parsing tag bodies: entities without semicolon are also replaced (default).
     *  2) attribute - emulates behavior of browser when parsing tag attributes: entities without semicolon are replaced when not followed by equality sign =.
     *  3) strict - ignores entities without semicolon.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Examples
     *  decode('&lt; &gt; &quot; &apos; &amp; &#169; &#8710;');
     *  // -> '< > " \' & © ∆'
     *
     *  decode('&copy;', {level: 'html5'});
     *  // -> '©'
     *
     *  decode('&copy;', {level: 'xml'});
     *  // -> '&copy;'
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} level
     * Default: 'all'
     * @param {string} scope
     * Default: 'body'
     * @returns {object}
     * Returns object:
     *  {
     *      level: {One of the following string values: 'all', 'html5', 'html4', or 'xml'},
     *      scope: {One of the following string values: 'body', 'attribute', 'strict'},
     *  }
     */
    static buildDecodingOptions(level = 'all', scope = 'body') {
        level = level || 'all';

        level = level.indexOf('all') ||
        level.indexOf('html5') ||
        level.indexOf('html4') ||
        level.indexOf('xml')
            ? level
            : 'all';

        scope = scope || 'body';

        scope = scope.indexOf('body') ||
        scope.indexOf('attribute') ||
        scope.indexOf('strict')
            ? scope
            : 'body';

        return {
            level,
            scope
        };
    }

    /**
     * @static
     * @public @method
     * Uses the encode(value, options) function from html-entities npm package to encode
     * the provided string with provided options.
     * See: EzString.buildEncodingOptions(...) for details on the encoding options that can be provided.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example:
     *  EzString.htmlEncode('< > " \' & © ∆')
     *  Result: '&lt; &gt; &quot; &apos; &amp; © ∆'
     *
     *  EzString.htmlEncode('< > " \' & © ∆');
     *  Result: '&lt; &gt; &quot; &apos; &amp; © ∆'
     *
     *  EzString.htmlEncode('< ©', {mode: 'nonAsciiPrintable'});
     *  Result: '&lt; &copy;'
     *
     *  EzString.htmlEncode('< ©', {mode: 'nonAsciiPrintable', level: 'xml'});
     *  Result: '&lt; &#169;'
     *
     *  EzString.htmlEncode('< > " \' & ©', {mode: 'nonAsciiPrintableOnly', level: 'xml'});
     *  Result: '< > " \' & &#169;'
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} rawString
     * @param {undefined|null|object} encodingOptions
     * Default encodingOptions is not provided:
     * EzString.buildEncodingOptions('all', 'specialChars', 'decimal') (aka {level: 'all', mode: 'specialChars', numeric: 'decimal'})
     * @returns {string}
     */
    static htmlEncode(rawString, encodingOptions) {
        if (!EzString.hasText(rawString) || EzNumber.ZERO_LENGTH === rawString?.length) {
            return EzString.EMPTY;
        }

        // Method encode from the html-entities library
        return encode(
            rawString,
            EzObject.isValid(encodingOptions)
                ? encodingOptions
                : EzString.buildEncodingOptions(
                    'all',
                    'specialChars',
                    'decimal'));
    }

    /**
     * @static
     * @public @method
     * Uses the encode(value, options) function from html-entities npm package to encode
     * the provided string with provided options.
     * Trims the encoded string before returning.
     * @param {string} [rawHTML=EzString.EMPTY]
     * @param {undefined|null|object} [encodingOptions=null]
     * Default encodingOptions is not provided:
     * EzString.buildEncodingOptions('all', 'specialChars', 'decimal') (aka {level: 'all', mode: 'specialChars', numeric: 'decimal'})
     * @returns {string}
     * Returns a trimmed HTML encoding of the provided rawHTML value.
     * If the provided rawHTML value is not a string or empty, then EzString.EMPTY is returned.
     */
    static htmlEncodeTrim(rawHTML = EzString.EMPTY, encodingOptions = null) {
        return EzString.trim(
            EzString.htmlEncode(rawHTML, encodingOptions));
    }

    /**
     * @static
     * @public @method
     * Uses the decode(value, options) function from html-entities npm package to decode
     * the provided string using the provided decoding options.
     * See: EzString.buildDecodingOptions(...) for details on the decoding options that can be provided.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Examples
     *  EzString.htmlDecode('&lt; &gt; &quot; &apos; &amp; &#169; &#8710;')
     *  Result: '< > " \' & © ∆'
     *
     *  EzString.htmlDecode('&lt; &gt; &quot; &apos; &amp; &#169; &#8710;');
     *  Result: '< > " \' & © ∆'
     *
     *  EzString.htmlDecode('&copy;', {level: 'html5'});
     *  Result: '©'
     *
     *  EzString.htmlDecode('&copy;', {level: 'xml'});
     *  Result: '&copy;'
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} [htmlEncodedValue=EzString.EMPTY]
     * @param {undefined|null|object} [decodingOptions=null]
     * Default value if not provided: EzString.buildDecodingOptions('all', 'body')  (aka {level: 'all', scope: 'body'})
     * @returns {string}
     */
    static htmlDecode(htmlEncodedValue = EzString.EMPTY, decodingOptions = null) {
        if (!EzString.hasText(htmlEncodedValue) || EzNumber.ZERO_LENGTH === htmlEncodedValue?.length) {
            return EzString.EMPTY;
        }

        // Method encode from the html-entities library
        return decode(
            htmlEncodedValue,
            EzObject.isValid(decodingOptions)
                ? decodingOptions
                : EzString.buildDecodingOptions('all', 'body'));
    }

    /**
     * @static
     * @public @method
     * Uses the decode(value, options) function from html-entities npm package to decode
     * the provided string using the provided decoding options.
     * See: EzString.buildDecodingOptions(...) for details on the decoding options that can be provided.
     * Trims the decoded string before returning.
     * @param {string} [htmlEncodedValue=EzString.EMPTY]
     * @param {undefined|null|object} [decodingOptions=null]
     * Default value if not provided: EzString.buildDecodingOptions('all', 'body')  (aka {level: 'all', scope: 'body'})
     * @returns {string}
     */
    static htmlDecodeTrim(htmlEncodedValue = EzString.EMPTY, decodingOptions = null) {
        return EzString.trim(
            EzString.htmlDecode(htmlEncodedValue, decodingOptions));
    }

    /**
     * @static
     * @public @method
     * Replaces occurances of some characters to prevent HTML injection.
     * If the provided value is undefined or null then an empty string is returned.
     * @param {undefined|null|string} value
     * @returns {string}
     * @deprecated
     * Migrate to: EzString.htmlEncode(...)
     */
    static simpleEncodeHtml(value) {
        return EzString.htmlEncode(value);
    }

    /**
     * @public @method
     * Replaces the &# character encoding tags with the actual characters.
     * @param {string} htmlString
     * @returns {string}
     */
    static encodeHtmlDelimitQuotes(htmlString) {
        if (!EzString.hasText(htmlString)) {
            return EzString.EMPTY;
        }

        let result = EzString.htmlEncode(result);

        for (const aChar of htmlString) {
            if (EzChar.quote === aChar) {
                result = result.replace(EzChar.quote, String.raw`\'`);
            } else if (EzChar.doubleQuote === aChar) {
                result = result.replace(EzChar.doubleQuote, String.raw`\"`);
            }
        }

        return result;
    }

    /**
     * @public @method
     * Replaces special HTML characters with their &# replacement value.
     * Alias for: EzString.htmlDecode(htmlEncodedString)
     * @param {String} htmlString
     * @returns {String}
     * @deprecated
     * Migrate to: EzString.htmlDecode(htmlEncodedString)
     */
    static decodeHtml(htmlEncodedString) {
        return EzString.htmlDecode(htmlEncodedString);
    }

    /**
     * @public @method
     * Replaces special HTML characters with their &# replacement value.
     * Alias for: EzString.htmlDecodeTrim(htmlEncodedString)
     * @param {String} htmlString
     * @returns {String}
     * @deprecated
     * Migrate to: EzString.htmlDecodeTrim(htmlEncodedString)
     */
    static decodeHtmlTrim(htmlEncodedString) {
        return EzString.htmlDecodeTrim(htmlEncodedString);
    }

    /**
     * @static
     * @public @method
     * Replaces special HTML characters with their &# replacement value.
     * Alias for: EzString.htmlEncode(rawHTML)
     * @param {string} rawHTML
     * @returns {string}
     * @deprecated
     * Migrate to: EzString.htmlEncode(rawHTML)
     */
    static encodeHtml(rawHTML) {
        return EzString.htmlEncode(rawHTML);
    }

    /**
     * @static
     * @public @method
     * Replaces special HTML characters with their &# replacement value.
     * Alias for: EzString.htmlEncodeTrim(rawHTML)
     * @param {string} rawHTML
     * @returns {string}
     * @deprecated
     * Migrate to: EzString.htmlEncodeTrim(rawHTML)
     */
    static encodeHtmlTrim(rawHTML) {
        return EzString.htmlEncodeTrim(rawHTML);
    }

    /**
     * @static
     * @public @method
     * Strips all whitespace characters from the provided aValue.
     * This includes all control characters and spaces.
     * @value {string}
     * @returns {string}
     */
    static stripWhitespace(aValue) {
        if (!EzString.hasLength(aValue)) {
            throw new EzBadParamException(
                { aValue },
                EzString,
                EzString.stripWhitespace);
        }

        return EzString.drop(
            aValue,
            EzRegEx.MATCH_ALL_WHITESPACE_CHARACTERS,
            EzString.EMPTY);
    }

    /**
     * @static
     * @public @method
     * Strips all control characters from the string (space is not removed)
     * @value {string}
     * @returns {string}
     */
    static stripControlCharacters(aValue) {
        if (!EzString.hasLength(aValue)) {
            throw new EzBadParamException(
                { aValue },
                EzString,
                EzString.stripControlCharacters);
        }

        return EzString.drop(
            EzRegEx.MATCH_CONTROL_CHARACTERS,
            EzString.EMPTY);
    }

    /**
     * @static
     * @public @method
     * Strips all occurances of two or more sequental spaces
     * Example:
     *  EzString.stripExtraSpaces('A sentence   with extra spaces.   ');
     *  Result: 'A sentence with extra spaces. ');
     * @value {string}
     * @returns {string}
     */
    static stripExtraSpaces(aValue) {
        if (!EzString.hasLength(aValue)) {
            throw new EzBadParamException(
                { aValue },
                EzString,
                EzString.stripExtraSpaces);
        }

        return EzString.drop(
            EzRegEx.MATCH_2_OR_MORE_SPACES,
            EzString.EMPTY);
    }

    /**
     * @static
     * @public @method
     * Calculates the Levenshtein edit distance between two strings: the minimum number of single
     * character insertions, deletions, or substitutions required to turn aValue into bValue.
     * Used to detect likely typos (e.g. comparing an email domain against a known provider domain).
     * @param {string} aValue
     * @param {string} bValue
     * @returns {number}
     */
    static levenshteinDistance(aValue, bValue) {
        const a = EzString.stringOrEmpty(aValue);

        const b = EzString.stringOrEmpty(bValue);

        const distanceRow = Array.from(
            { length: b.length + EzNumber.ONE },
            (_unused, columnIndex) => columnIndex);

        for (let rowIndex = EzNumber.ONE; rowIndex <= a.length; rowIndex++) {
            let previousDiagonal = distanceRow[EzNumber.ZERO];

            distanceRow[EzNumber.ZERO] = rowIndex;

            for (let columnIndex = EzNumber.ONE; columnIndex <= b.length; columnIndex++) {
                const previousUpperLeft = previousDiagonal;

                previousDiagonal = distanceRow[columnIndex];

                distanceRow[columnIndex] = a[rowIndex - EzNumber.ONE] === b[columnIndex - EzNumber.ONE]
                    ? previousUpperLeft
                    : EzNumber.ONE + Math.min(
                        previousUpperLeft,
                        distanceRow[columnIndex],
                        distanceRow[columnIndex - EzNumber.ONE]);
            }
        }

        return distanceRow[b.length];
    }
}
